import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { documentacaoApi, DocFile } from '../api/documentacao'
import {
  FileText,
  Folder,
  Search,
  BookOpen,
  Info,
  Lightbulb,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  Menu,
} from 'lucide-react'

// Interface para blocos Markdown analisados
interface Block {
  type: 'h1' | 'h2' | 'h3' | 'h4' | 'paragraph' | 'list' | 'code' | 'alert' | 'table'
  content: string | string[]
  lang?: string
  alertType?: 'note' | 'tip' | 'warning' | 'important' | 'caution'
  listItems?: string[]
  headers?: string[]
  rows?: string[][]
}

// Helper para converter estilos inline em React nodes
const renderInlineText = (text: string): React.ReactNode => {
  if (!text) return ''

  const parts: React.ReactNode[] = []
  let remaining = text
  let keyIdx = 0

  while (remaining) {
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/)
    const codeMatch = remaining.match(/`([^`]+)`/)
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)

    const matches = [
      boldMatch ? { match: boldMatch, type: 'bold', index: boldMatch.index! } : null,
      codeMatch ? { match: codeMatch, type: 'code', index: codeMatch.index! } : null,
      linkMatch ? { match: linkMatch, type: 'link', index: linkMatch.index! } : null,
    ].filter((m): m is { match: RegExpMatchArray; type: string; index: number } => m !== null)

    if (matches.length === 0) {
      parts.push(<React.Fragment key={keyIdx++}>{remaining}</React.Fragment>)
      break
    }

    matches.sort((a, b) => a.index - b.index)
    const first = matches[0]

    if (first.index > 0) {
      parts.push(<React.Fragment key={keyIdx++}>{remaining.substring(0, first.index)}</React.Fragment>)
    }

    const matchText = first.match[0]
    const content = first.match[1]

    if (first.type === 'bold') {
      parts.push(
        <strong key={keyIdx++} className="font-semibold text-slate-900 dark:text-white">
          {content}
        </strong>
      )
    } else if (first.type === 'code') {
      parts.push(
        <code
          key={keyIdx++}
          className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-xs text-rose-600 dark:text-rose-400 border border-slate-200/50 dark:border-slate-800/50"
        >
          {content}
        </code>
      )
    } else if (first.type === 'link') {
      const url = first.match[2]
      parts.push(
        <a
          key={keyIdx++}
          href={url}
          target={url.startsWith('http') ? '_blank' : '_self'}
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
        >
          {content}
        </a>
      )
    }

    remaining = remaining.substring(first.index + matchText.length)
  }

  return parts
}

// Analisador personalizado de Markdown
const parseMarkdown = (text: string): Block[] => {
  const lines = text.split('\n')
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // 1. Blocos de Código
    if (line.trim().startsWith('```')) {
      const lang = line.trim().substring(3).trim()
      const codeLines: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      blocks.push({
        type: 'code',
        content: codeLines.join('\n'),
        lang: lang || 'text',
      })
      i++
      continue
    }

    // 2. Blocos de Alerta (GitHub Style)
    if (line.trim().startsWith('> [!')) {
      const match = line.match(/>\s*\[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)\]/i)
      if (match) {
        const alertType = match[1].toLowerCase() as any
        const alertLines: string[] = []
        i++
        while (i < lines.length && (lines[i].trim().startsWith('>') || lines[i].trim() === '')) {
          let contentLine = lines[i].trim()
          if (contentLine.startsWith('>')) {
            contentLine = contentLine.substring(1).trim()
          }
          alertLines.push(contentLine)
          i++
        }
        blocks.push({
          type: 'alert',
          content: alertLines.join('\n'),
          alertType,
        })
        continue
      }
    }

    // 3. Títulos (Headers)
    if (line.startsWith('# ')) {
      blocks.push({ type: 'h1', content: line.substring(2).trim() })
      i++
      continue
    }
    if (line.startsWith('## ')) {
      blocks.push({ type: 'h2', content: line.substring(3).trim() })
      i++
      continue
    }
    if (line.startsWith('### ')) {
      blocks.push({ type: 'h3', content: line.substring(4).trim() })
      i++
      continue
    }
    if (line.startsWith('#### ')) {
      blocks.push({ type: 'h4', content: line.substring(5).trim() })
      i++
      continue
    }

    // 4. Listas
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      const listItems: string[] = []
      while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))) {
        listItems.push(lines[i].trim().substring(2).trim())
        i++
      }
      blocks.push({ type: 'list', content: '', listItems })
      continue
    }

    // 5. Tabelas
    if (line.trim().startsWith('|')) {
      const headers = line
        .split('|')
        .map((s) => s.trim())
        .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)
      i++
      if (i < lines.length && lines[i].trim().startsWith('|') && lines[i].includes('-')) {
        i++ // pular separador (|---|---|)
        const rows: string[][] = []
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          const row = lines[i]
            .split('|')
            .map((s) => s.trim())
            .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)
          rows.push(row)
          i++
        }
        blocks.push({ type: 'table', content: '', headers, rows })
        continue
      } else {
        i--
      }
    }

    // 6. Linha vazia
    if (line.trim() === '') {
      i++
      continue
    }

    // 7. Parágrafo Padrão
    blocks.push({ type: 'paragraph', content: line })
    i++
  }

  return blocks
}

// Componente premium de Bloco de Código com cópia rápida
const CodeBlock: React.FC<{ code: string; lang: string }> = ({ code, lang }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative my-5 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-900 dark:bg-slate-950">
      <div className="flex justify-between items-center px-4 py-2 bg-slate-900 border-b border-slate-800 text-xs text-slate-400 font-mono">
        <span>{lang.toUpperCase()}</span>
        <button
          onClick={handleCopy}
          className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-white font-medium transition-colors cursor-pointer"
        >
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto font-mono text-sm leading-relaxed whitespace-pre text-slate-100 bg-slate-900 dark:bg-slate-950">
        <code>{code}</code>
      </pre>
    </div>
  )
}

// Componente premium de Alertas (Callouts)
const AlertBlock: React.FC<{ content: string; type: 'note' | 'tip' | 'warning' | 'important' | 'caution' }> = ({
  content,
  type,
}) => {
  const styles = {
    note: {
      bg: 'bg-blue-50/50 dark:bg-blue-950/20',
      border: 'border-l-4 border-blue-500',
      text: 'text-blue-800 dark:text-blue-300',
      icon: <Info size={18} className="text-blue-500 shrink-0" />,
      label: 'Nota',
    },
    tip: {
      bg: 'bg-emerald-50/50 dark:bg-emerald-950/20',
      border: 'border-l-4 border-emerald-500',
      text: 'text-emerald-800 dark:text-emerald-300',
      icon: <Lightbulb size={18} className="text-emerald-500 shrink-0" />,
      label: 'Dica',
    },
    warning: {
      bg: 'bg-amber-50/50 dark:bg-amber-950/20',
      border: 'border-l-4 border-amber-500',
      text: 'text-amber-800 dark:text-amber-300',
      icon: <AlertTriangle size={18} className="text-amber-500 shrink-0" />,
      label: 'Aviso',
    },
    important: {
      bg: 'bg-violet-50/50 dark:bg-violet-950/20',
      border: 'border-l-4 border-violet-500',
      text: 'text-violet-800 dark:text-violet-300',
      icon: <Info size={18} className="text-violet-500 shrink-0" />,
      label: 'Importante',
    },
    caution: {
      bg: 'bg-red-50/50 dark:bg-red-950/20',
      border: 'border-l-4 border-red-500',
      text: 'text-red-800 dark:text-red-300',
      icon: <AlertCircle size={18} className="text-red-500 shrink-0" />,
      label: 'Cuidado',
    },
  }

  const s = styles[type] || styles.note

  return (
    <div className={`p-4 my-5 rounded-r-lg ${s.bg} ${s.border} flex gap-3`}>
      {s.icon}
      <div>
        <span className={`text-sm font-semibold uppercase tracking-wider block ${s.text}`}>
          {s.label}
        </span>
        <div className={`mt-1 text-sm leading-relaxed ${s.text}`}>
          {content.split('\n').map((line, idx) => (
            <p key={idx} className="mt-1">
              {renderInlineText(line)}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

const DocumentacaoPage: React.FC = () => {
  const { docType } = useParams<{ docType: string }>()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Carregar lista de documentações
  const { data: categories = [], isLoading: isLoadingList } = useQuery({
    queryKey: ['documentacao-lista'],
    queryFn: () => documentacaoApi.list(),
  })

  // Carregar conteúdo do arquivo selecionado
  const { data: fileContent, isLoading: isLoadingFile } = useQuery({
    queryKey: ['documentacao-conteudo', selectedFilePath],
    queryFn: () => documentacaoApi.getFile(selectedFilePath!),
    enabled: !!selectedFilePath,
  })

  // Filtro de arquivos por tipo de documento (Thor vs Galatea Auth)
  const isThorDocument = (file: DocFile, category: string) => {
    const name = file.title.toLowerCase()
    const cat = category.toLowerCase()
    return (
      name.includes('thor') ||
      name.includes('dicionario') ||
      name.includes('basicas') ||
      cat.includes('dicionario') ||
      cat.includes('thor')
    )
  }

  // Filtrar e processar a lista de categorias baseada na rota e termo de busca
  const filteredCategories = useMemo(() => {
    const isThorRoute = docType === 'thor'

    return categories
      .map((cat) => {
        // Filtrar arquivos da categoria
        const files = cat.files.filter((file) => {
          // Filtrar pela rota (Thor vs Auth)
          const matchesRoute = isThorRoute ? isThorDocument(file, cat.category) : !isThorDocument(file, cat.category)
          // Filtrar pela busca
          const matchesSearch =
            file.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cat.category.toLowerCase().includes(searchTerm.toLowerCase())

          return matchesRoute && matchesSearch
        })

        return { ...cat, files }
      })
      .filter((cat) => cat.files.length > 0)
  }, [categories, docType, searchTerm])

  // Selecionar primeiro arquivo automaticamente se houver e nada estiver selecionado
  useEffect(() => {
    if (filteredCategories.length > 0 && !selectedFilePath) {
      const firstCat = filteredCategories[0]
      if (firstCat.files.length > 0) {
        setSelectedFilePath(firstCat.files[0].path)
      }
    }
  }, [filteredCategories, selectedFilePath])

  // Resetar seleção ao mudar a rota (Thor vs Auth)
  useEffect(() => {
    setSelectedFilePath(null)
  }, [docType])

  // Parsear markdown quando o conteúdo é carregado
  const parsedBlocks = useMemo(() => {
    if (!fileContent?.content) return []
    return parseMarkdown(fileContent.content)
  }, [fileContent])

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-6rem)] w-full gap-6 px-4 py-6">
      {/* Botão de Menu Lateral no Mobile */}
      <div className="lg:hidden flex items-center justify-between bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-sm">
        <span className="font-semibold text-slate-800 dark:text-slate-200">
          {docType === 'thor' ? 'Manuais ThorJava' : 'Arquitetura Galatea'}
        </span>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Barra Lateral de Navegação (Categorias / Arquivos) */}
      <aside
        className={`
          ${mobileMenuOpen ? 'block' : 'hidden'} lg:block
          w-full lg:w-80 shrink-0 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 shadow-sm flex flex-col gap-4
        `}
      >
        <div className="relative">
          <TextField
            size="small"
            placeholder="Pesquisar manuais..."
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search size={16} className="text-slate-400 mr-2" />,
            }}
          />
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-standard max-h-[calc(100vh-14rem)] pr-1">
          {isLoadingList ? (
            <div className="text-sm text-slate-500 text-center py-8">Carregando índice...</div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">Nenhum manual encontrado.</div>
          ) : (
            <div className="space-y-4">
              {filteredCategories.map((cat) => (
                <div key={cat.folder} className="space-y-1">
                  <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <Folder size={12} className="text-blue-500" />
                    <span>{cat.category}</span>
                  </div>
                  <div className="space-y-0.5 pl-2">
                    {cat.files.map((file) => {
                      const isSelected = selectedFilePath === file.path
                      return (
                        <button
                          key={file.path}
                          onClick={() => {
                            setSelectedFilePath(file.path)
                            setMobileMenuOpen(false)
                          }}
                          className={`
                            w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                            ${
                              isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
                            }
                          `}
                        >
                          <FileText size={14} className={isSelected ? 'text-blue-500' : 'text-slate-400'} />
                          <span className="truncate">{file.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Conteúdo Principal (Markdown Viewer) */}
      <main className="flex-1 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 shadow-sm flex flex-col min-w-0">
        {isLoadingFile ? (
          <div className="flex flex-col items-center justify-center flex-1 py-20 gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            <span className="text-sm text-slate-500">Carregando conteúdo...</span>
          </div>
        ) : !selectedFilePath ? (
          <div className="flex flex-col items-center justify-center flex-1 py-20 text-center gap-4">
            <BookOpen size={48} className="text-slate-300 dark:text-slate-700" />
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-200">Nenhum Documento Selecionado</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-xs">
                Selecione um manual na barra lateral esquerda para visualizar suas especificações.
              </p>
            </div>
          </div>
        ) : (
          <article className="prose dark:prose-invert max-w-none">
            {/* Header do Documento */}
            <div className="border-b border-slate-200 dark:border-slate-800 pb-4 mb-6">
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">
                <span>Guias Técnicos</span>
                <ChevronRight size={12} />
                <span>{docType === 'thor' ? 'ThorJava' : 'Galatea Auth'}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                {fileContent?.title}
              </h1>
              <p className="text-xs text-slate-400 font-mono mt-1">Caminho: {fileContent?.path}</p>
            </div>

            {/* Corpo do Documento Analisado */}
            <div className="space-y-4 text-slate-800 dark:text-slate-300">
              {parsedBlocks.map((block, idx) => {
                switch (block.type) {
                  case 'h1':
                    return (
                      <h2
                        key={idx}
                        className="text-2xl font-bold text-slate-900 dark:text-white pt-6 border-b border-slate-100 dark:border-slate-800 pb-2"
                      >
                        {renderInlineText(block.content as string)}
                      </h2>
                    )
                  case 'h2':
                    return (
                      <h3 key={idx} className="text-xl font-semibold text-slate-950 dark:text-white pt-4">
                        {renderInlineText(block.content as string)}
                      </h3>
                    )
                  case 'h3':
                    return (
                      <h4 key={idx} className="text-lg font-medium text-slate-900 dark:text-slate-200 pt-3">
                        {renderInlineText(block.content as string)}
                      </h4>
                    )
                  case 'h4':
                    return (
                      <h5 key={idx} className="text-base font-medium text-slate-900 dark:text-slate-300 pt-2">
                        {renderInlineText(block.content as string)}
                      </h5>
                    )
                  case 'paragraph':
                    return (
                      <p key={idx} className="leading-relaxed text-sm">
                        {renderInlineText(block.content as string)}
                      </p>
                    )
                  case 'list':
                    return (
                      <ul key={idx} className="list-disc pl-5 space-y-1.5 text-sm my-3">
                        {block.listItems?.map((item, itemIdx) => (
                          <li key={itemIdx}>{renderInlineText(item)}</li>
                        ))}
                      </ul>
                    )
                  case 'code':
                    return <CodeBlock key={idx} code={block.content as string} lang={block.lang || 'text'} />
                  case 'alert':
                    return (
                      <AlertBlock
                        key={idx}
                        content={block.content as string}
                        type={block.alertType || 'note'}
                      />
                    )
                  case 'table':
                    return (
                      <div key={idx} className="overflow-x-auto my-5 border border-slate-200 dark:border-slate-800 rounded-lg">
                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-sm">
                          <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr>
                              {block.headers?.map((header, hIdx) => (
                                <th
                                  key={hIdx}
                                  className="px-4 py-2.5 text-left font-semibold text-slate-900 dark:text-white"
                                >
                                  {renderInlineText(header)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                            {block.rows?.map((row, rIdx) => (
                              <tr key={rIdx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                {row.map((cell, cIdx) => (
                                  <td key={cIdx} className="px-4 py-2 text-slate-700 dark:text-slate-300">
                                    {renderInlineText(cell)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  default:
                    return null
                }
              })}
            </div>
          </article>
        )}
      </main>
    </div>
  )
}

// Para compatibilidade com import TextField do MUI que foi omitido nos imports locais
const TextField: React.FC<{
  size?: 'small' | 'medium'
  placeholder?: string
  fullWidth?: boolean
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  InputProps?: {
    startAdornment?: React.ReactNode
  }
}> = ({ placeholder, fullWidth, value, onChange, InputProps }) => {
  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      {InputProps?.startAdornment && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {InputProps.startAdornment}
        </div>
      )}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`
          ${InputProps?.startAdornment ? 'pl-9' : 'pl-3'}
          pr-3 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full transition-all
        `}
      />
    </div>
  )
}

export default DocumentacaoPage
